import type { TransportItemType } from "@grafana/faro-web-sdk";
import { getPageLabel, isRegisteredRouteName } from "./route-names";

// This is the complete outbound vocabulary. Never add free-form context here.
export const outcomes = ["completed", "canceled", "timeout", "backgrounded"] as const;
export type Outcome = (typeof outcomes)[number];

export type Measurement = {
  type: string;
  timestamp: string;
  values: Record<string, number>;
  context: Record<string, string>;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

export interface EntityIds {
  app_id?: string;
  customer_id?: string;
}

export function entityIds(input: { app_id?: unknown; customer_id?: unknown }): EntityIds {
  const ids: EntityIds = {};

  for (const key of ["app_id", "customer_id"] as const) {
    const value = input[key];
    if (typeof value === "string") {
      ids[key] = value;
    }
  }

  return ids;
}

// One outbound boundary: copy only our measurement vocabulary and fresh app metadata.
export function createPrivacyFilter(environment: string, version: string) {
  return (item: unknown) => {
    if (!isRecord(item) || item.type !== "measurement" || !isRecord(item.payload)) {
      return null;
    }
    const { type, timestamp, values, context } = item.payload;
    if (
      type !== "page_load_complete" ||
      typeof timestamp !== "string" ||
      !Number.isFinite(Date.parse(timestamp)) ||
      !isRecord(values) ||
      !isRecord(context)
    ) {
      return null;
    }

    const { page, attempt_id, navigation_type, outcome } = context;
    const duration = values.duration_ms;
    if (
      typeof page !== "string" ||
      (page !== "other" && !isRegisteredRouteName(page)) ||
      typeof attempt_id !== "string" ||
      (navigation_type !== "initial" && navigation_type !== "client") ||
      !outcomes.includes(outcome as Outcome) ||
      typeof duration !== "number" ||
      !Number.isFinite(duration) ||
      duration < 0
    ) {
      return null;
    }

    return {
      type: "measurement" as TransportItemType.MEASUREMENT,
      payload: {
        type,
        timestamp,
        values: { duration_ms: duration },
        context: {
          page,
          page_name: getPageLabel(page),
          attempt_id,
          navigation_type,
          outcome: outcome as Outcome,
          ...entityIds(context),
          ...(typeof context.journey_id === "string" ? { journey_id: context.journey_id } : {}),
        },
      },
      meta: { app: { name: "vendor-web", environment, version } },
    };
  };
}
